import { compact } from 'lodash';

import { AIOnboardingModalTestamentaryEntityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import {
  getAssociatedDocumentsForSuggestionID,
  getValidDocumentIds,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/common.utils';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getKgV2NodeIDFromSuggestion } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import {
  AugmentedCreateTestamentaryEntityInput,
  EntityInEstateStatus,
} from '@/types/schema';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

/**
 * Maps the ONBOARDING_TESTAMENTARY_ENTITY suggestion to form data.
 *
 * @param suggestions
 * @param isSingleClientHousehold
 */
export function mapTestamentaryEntitiesToFormData(
  suggestions: AiSuggestionForReviewCard[],
  { isTwoGrantor }: AiOnboardingModal_HouseholdFragment
): AIOnboardingModalTestamentaryEntityField[] {
  return compact(
    suggestions.map((s) => {
      if (!s.onboardingTestamentaryEntity) return null;
      return {
        _suggestionID: s.id,
        testamentaryEntityName:
          s.onboardingTestamentaryEntity?.displayName ??
          sentenceCaseWithAcronyms(
            s.onboardingTestamentaryEntity?.testamentaryEntityName || ''
          ),
        testamentaryEntityKind:
          s.onboardingTestamentaryEntity?.testamentaryEntityKind || '',
        testamentaryEntityAssetLocation: isTwoGrantor
          ? s.onboardingTestamentaryEntity?.inEstateStatus || ''
          : EntityInEstateStatus.OutOfEstate, // single-client households already create TE's as out-of-estate
      };
    })
  );
}

export function getEmptyTestamentaryEntityField(): AIOnboardingModalTestamentaryEntityField {
  return {
    _suggestionID: '',
    testamentaryEntityName: '',
    testamentaryEntityKind: '',
    testamentaryEntityAssetLocation: '',
  };
}

export function getMissingRequiredTestamentaryEntityFields(
  testamentaryEntity: AIOnboardingModalTestamentaryEntityField
): string[] {
  return compact([
    !testamentaryEntity.testamentaryEntityName && 'a sub-trust name',
    !testamentaryEntity.testamentaryEntityKind && 'a sub-trust type',
    !testamentaryEntity.testamentaryEntityAssetLocation && 'an asset location',
  ]);
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

export function mapTestamentaryEntitiesToInput(
  testamentaryEntities: AIOnboardingModalTestamentaryEntityField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): AugmentedCreateTestamentaryEntityInput[] {
  const validDocumentIds = getValidDocumentIds(household);

  return testamentaryEntities.map((t) => {
    if (!t.testamentaryEntityKind) {
      throw new Error('Testamentary entity kind is required');
    }
    if (!t.testamentaryEntityAssetLocation) {
      throw new Error('Testamentary entity asset location is required');
    }

    // Old additional document assignments
    // Filter out document IDs that are no longer in the household (soft-deleted).
    // The mutation will fail if we try to create a relationship with a deleted document.
    const additionalDocumentIDs = getAssociatedDocumentsForSuggestionID(
      t._suggestionID,
      suggestionsByID
    )
      .map((d) => d.docID)
      .filter((docID) => validDocumentIds.has(docID));

    const suggestion = t._suggestionID
      ? suggestionsByID[t._suggestionID]
      : undefined;
    const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);
    const notes = suggestion?.onboardingTestamentaryEntity?.notes;

    return {
      create: {
        householdID: household.id,
        displayName: t.testamentaryEntityName,
        description: notes || undefined,
        kind: t.testamentaryEntityKind,
        onboardingTestamentaryEntitySuggestionIDs: t._suggestionID
          ? [t._suggestionID]
          : undefined,
        additionalDocumentIDs,
        inEstateStatus: t.testamentaryEntityAssetLocation,
        survivingSpouseStateInEstateStatus: t.testamentaryEntityAssetLocation,
        kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
      },
    };
  });
}
